<template>
  <n-dropdown
    placement="bottom-start"
    trigger="manual"
    :x="x"
    :y="y"
    :options="contextMenuOptions"
    :show="dropdownVisible"
    @select="handleContextMenuSelect"
    @clickoutside="handleClickOutside"
  />
</template>

<script setup lang="ts">
/**
 * 设计树节点右键菜单组件。
 *
 * 文件定位：
 * 1. 该组件统一承载设计树节点上下文菜单；
 * 2. 图形区右键命中装配组件时，也只解析到同一个设计树节点 key 后复用该组件；
 * 3. 菜单项、只读过滤与动作分发集中在这里，避免设计树右键和图形区右键产生行为分叉。
 */
import { computed, h } from 'vue';
import {
  NDropdown,
  type DropdownOption,
  type TreeOption,
} from 'naive-ui';
import { storeToRefs } from 'pinia';
import { AssemblyComponentFixationState } from '@puppycad/cad-core';
import { useFeatureTreeStore } from '@/hooks/useFeatureTree';
import { getFeatureTreeNodeKey } from '@/hooks/feature-tree/resolve-feature-tree-expanded-keys';
import {
  FeatureTreeNodeKind,
  type FeatureTreeNodeMeta,
} from '@/hooks/feature-tree/feature-tree-types';
import FeatureTreeNodeIcon from '@/components/editor/FeatureTreeNodeIcon.vue';
import { useVersionWorkspaceStore } from '@/stores/version-workspace';

/**
 * 设计树节点右键菜单组件属性。
 */
interface FeatureTreeNodeContextMenuProps {
  /** 菜单是否显示。 */
  show: boolean;
  /** 菜单弹出位置 X 坐标。 */
  x: number;
  /** 菜单弹出位置 Y 坐标。 */
  y: number;
  /** 当前右键目标设计树节点 key。 */
  targetKey: string;
  /** 当前右键目标节点类型；图形区入口可省略，由节点元数据兜底。 */
  nodeType?: string;
  /** datum-plane 节点对应的基准面 ID。 */
  planeId?: string;
  /** 同一节点连续右键时用于强制刷新普通 Map 状态的版本号。 */
  revision?: number;
  /** 是否处于历史版本只读预览态。 */
  readonlyPreview?: boolean;
}

/**
 * 设计树节点右键菜单组件事件。
 */
interface FeatureTreeNodeContextMenuEmits {
  /**
   * 更新菜单显示状态。
   * @param event 事件名。
   * @param value 菜单是否显示。
   */
  (event: 'update:show', value: boolean): void;
}

const props = withDefaults(defineProps<FeatureTreeNodeContextMenuProps>(), {
  nodeType: '',
  planeId: '',
  revision: 0,
  readonlyPreview: false,
});
const emit = defineEmits<FeatureTreeNodeContextMenuEmits>();

const featureTreeStore = useFeatureTreeStore();
const versionWorkspaceStore = useVersionWorkspaceStore();
const {
  featureTreeData,
  showFlatTreeView,
} = storeToRefs(featureTreeStore);

/**
 * 只读历史预览态下会修改模型或命令状态的菜单键。
 */
const READONLY_PREVIEW_BLOCKED_CONTEXT_MENU_KEYS = new Set([
  'create-datum-plane',
  'enter-sketch',
  'edit-sketch',
  'edit-mate',
  'copy-sketch',
  'edit-feature',
  'rename',
  'delete',
  'fix-component',
  'float-component',
  'hide',
  'show',
  'rollback-to-feature',
  'roll-forward',
  'roll-to-previous',
  'roll-to-end',
  'suppress-mate',
  'unsuppress-mate',
  'lock-concentric-rotation',
]);

/**
 * 线性零部件阵列运行时节点类型。
 */
enum LinearComponentPatternRuntimeNodeType {
  /** 阵列分组节点。 */
  Group = 'LinearPatternRuntimeGroup',
}

const isReadonlyPreview = computed(() => (
  props.readonlyPreview || versionWorkspaceStore.isReadonlyPreview === true
));

/**
 * 在树节点列表中查找指定 key 的节点。
 *
 * @param nodes 节点列表。
 * @param key 目标节点 key。
 * @returns 命中的树节点；未命中时返回 `null`。
 */
function findTreeNodeByKey(nodes: readonly TreeOption[], key: string): TreeOption | null {
  for (const node of nodes) {
    if (getFeatureTreeNodeKey(node) === key) {
      return node;
    }
    const matchedChild = Array.isArray(node.children)
      ? findTreeNodeByKey(node.children, key)
      : null;
    if (matchedChild) {
      return matchedChild;
    }
  }

  return null;
}

const contextMenuNodeMeta = computed<FeatureTreeNodeMeta | undefined>(() => {
  /**
   * featureTreeNodeMap 是普通 Map，不会被 Vue 追踪。revision 由调用方在每次右键时递增，
   * 确保同一节点刷新固定/浮动、隐藏/显示后再次右键可以读取最新元数据。
   */
  void props.revision;
  return featureTreeStore.featureTreeNodeMap.get(props.targetKey);
});

const contextMenuTargetNode = computed<TreeOption | null>(() => {
  void props.revision;
  if (!props.targetKey) {
    return null;
  }
  return findTreeNodeByKey(featureTreeData.value, props.targetKey);
});

const contextMenuNodeType = computed(() => {
  if (props.nodeType.length > 0) {
    return props.nodeType;
  }
  const targetRecord = contextMenuTargetNode.value as Record<string, unknown> | null;
  if (typeof targetRecord?.type === 'string' && targetRecord.type.length > 0) {
    return targetRecord.type;
  }

  const nodeMeta = contextMenuNodeMeta.value;
  if (nodeMeta?.kind === FeatureTreeNodeKind.DatumPlane) {
    return 'datum-plane';
  }
  if (nodeMeta?.kind === FeatureTreeNodeKind.DatumAxis) {
    return 'datum-axis';
  }
  if (nodeMeta?.kind === FeatureTreeNodeKind.DatumPoint) {
    return 'datum-point';
  }
  if (nodeMeta?.kind === FeatureTreeNodeKind.AssemblyMate) {
    return 'assembly-mate';
  }
  if (nodeMeta?.kind === FeatureTreeNodeKind.AssemblyMateFolder) {
    return 'assembly-mate-folder';
  }
  if (nodeMeta?.kind === FeatureTreeNodeKind.GlbDatumSlot) {
    return 'glb-datum-slot';
  }
  if (nodeMeta?.objectType === 'sketch') {
    return 'sketch';
  }
  return '';
});

const contextMenuPlaneId = computed(() => {
  if (props.planeId.length > 0) {
    return props.planeId;
  }
  return contextMenuNodeType.value === 'datum-plane'
    ? props.targetKey.replace('datum-plane-', '')
    : '';
});

const isLinearComponentPatternContextMenuGroup = computed(() => {
  const targetNode = contextMenuTargetNode.value as Record<string, unknown> | null;
  return targetNode?.linearComponentPatternRuntimeNodeType === LinearComponentPatternRuntimeNodeType.Group;
});

/**
 * 读取当前右键节点在设计树中的显示文本。
 *
 * @returns 节点标签文本。
 */
function resolveContextMenuTargetLabel(): string {
  const label = contextMenuTargetNode.value?.label;
  if (typeof label === 'string' && label.length > 0) {
    return label;
  }
  const displayName = contextMenuNodeMeta.value?.displayName;
  if (displayName && displayName.length > 0) {
    return displayName;
  }
  return props.targetKey;
}

/**
 * 判断设计树节点是否处于隐藏状态。
 *
 * @param node 设计树节点。
 * @param meta 设计树节点元数据。
 * @returns 节点是否隐藏。
 */
function isTreeNodeHidden(node: TreeOption | null, meta: FeatureTreeNodeMeta | undefined): boolean {
  return (node as { statusHidden?: unknown } | null)?.statusHidden === true || meta?.statusHidden === true;
}

/**
 * 判断设计树节点是否处于抑制状态。
 *
 * @param node 设计树节点。
 * @returns 节点是否被抑制。
 */
function isTreeNodeSuppressed(node: TreeOption | null): boolean {
  return (node as { statusSuppressed?: unknown } | null)?.statusSuppressed === true;
}

/**
 * 渲染设计树语义图标。
 *
 * @param iconType 图标语义类型。
 * @param size 图标尺寸。
 * @returns Carbon 图标节点。
 */
function renderFeatureTreeIcon(iconType: string, size = 14) {
  return h(FeatureTreeNodeIcon, { iconType, size });
}

/**
 * 创建右键菜单图标渲染器。
 *
 * @param iconType 图标语义类型。
 * @returns 菜单图标渲染函数。
 */
function createFeatureTreeMenuIcon(iconType: string) {
  return () => renderFeatureTreeIcon(iconType, 14);
}

/**
 * 构建单一隐藏/显示菜单项。
 *
 * @param node 右键命中的树节点。
 * @param meta 右键命中的树节点元数据。
 * @returns 隐藏或显示菜单项。
 */
function createVisibilityMenuOption(
  node: TreeOption | null,
  meta: FeatureTreeNodeMeta | undefined,
): DropdownOption {
  const isHidden = isTreeNodeHidden(node, meta);
  return {
    label: isHidden ? '显示' : '隐藏',
    key: isHidden ? 'show' : 'hide',
    icon: createFeatureTreeMenuIcon(isHidden ? 'action-show' : 'action-hide'),
  };
}

/**
 * 构建 Mate 抑制/解除抑制菜单项。
 *
 * @param node 右键命中的 Mate 节点。
 * @returns 抑制或解除抑制菜单项。
 */
function createAssemblyMateSuppressMenuOption(node: TreeOption | null): DropdownOption {
  const isSuppressed = isTreeNodeSuppressed(node);
  return {
    label: isSuppressed ? '解除抑制' : '抑制',
    key: isSuppressed ? 'unsuppress-mate' : 'suppress-mate',
    icon: createFeatureTreeMenuIcon(isSuppressed ? 'action-show' : 'status-suppressed'),
  };
}

/**
 * 清理过滤后多余的分隔线。
 *
 * @param options 已完成动作过滤的菜单项。
 * @returns 不含首尾分隔线与连续分隔线的菜单项。
 */
function removeRedundantContextMenuDividers(options: DropdownOption[]): DropdownOption[] {
  const normalizedOptions: DropdownOption[] = [];
  let previousIsDivider = true;

  options.forEach((option) => {
    const isDivider = option.type === 'divider';
    if (isDivider && previousIsDivider) {
      return;
    }
    normalizedOptions.push(option);
    previousIsDivider = isDivider;
  });

  if (normalizedOptions[normalizedOptions.length - 1]?.type === 'divider') {
    normalizedOptions.pop();
  }

  return normalizedOptions;
}

/**
 * 历史版本预览态过滤会修改模型或命令状态的菜单项。
 *
 * @param options 原始菜单项。
 * @returns 只读预览态下的安全菜单项。
 */
function filterReadonlyPreviewContextMenuOptions(options: DropdownOption[]): DropdownOption[] {
  if (!isReadonlyPreview.value) {
    return options;
  }

  const filteredOptions = options
    .map((option) => {
      if (typeof option.key !== 'string') {
        return option;
      }
      if (READONLY_PREVIEW_BLOCKED_CONTEXT_MENU_KEYS.has(option.key)) {
        return null;
      }
      if (Array.isArray(option.children)) {
        return {
          ...option,
          children: option.children.filter((child) => (
            typeof child.key !== 'string'
            || !READONLY_PREVIEW_BLOCKED_CONTEXT_MENU_KEYS.has(child.key)
          )),
        };
      }
      return option;
    })
    .filter((option): option is DropdownOption => Boolean(option));

  return removeRedundantContextMenuDividers(filteredOptions);
}

/**
 * 判断右键菜单动作在历史版本预览态是否需要阻断。
 *
 * @param key 右键菜单动作 key。
 * @returns 只读预览态下若该动作会改变 CAD 状态，则返回 true。
 */
function isReadonlyPreviewBlockedContextMenuKey(key: string | number): boolean {
  return isReadonlyPreview.value && READONLY_PREVIEW_BLOCKED_CONTEXT_MENU_KEYS.has(String(key));
}

/**
 * 构建当前命中节点的右键菜单项。
 *
 * @returns 菜单项列表。
 */
function buildContextMenuOptions(): DropdownOption[] {
  const nodeMeta = contextMenuNodeMeta.value;
  const targetNode = contextMenuTargetNode.value;
  const nodeType = contextMenuNodeType.value;
  const visibilityOption = createVisibilityMenuOption(targetNode, nodeMeta);

  if (nodeType === 'part-root') {
    return [
      {
        label: 'Tree Display',
        key: 'tree-display',
        icon: createFeatureTreeMenuIcon('action-tree-display'),
        children: [
          {
            label: 'Show Flat Tree View',
            key: 'toggle-flat-tree-view',
            icon: createFeatureTreeMenuIcon(showFlatTreeView.value ? 'status-checked' : 'action-tree-display'),
          },
        ],
      },
    ];
  }

  if (nodeType === 'rollback-bar') {
    return [
      {
        label: '退回到前',
        key: 'roll-to-previous',
        icon: createFeatureTreeMenuIcon('action-roll-previous'),
      },
      {
        label: '退回到尾',
        key: 'roll-to-end',
        icon: createFeatureTreeMenuIcon('action-roll-end'),
      },
    ];
  }

  if (nodeType === 'group') {
    if (props.targetKey === 'datum-planes-group') {
      return [
        {
          label: '新增基准面',
          key: 'create-datum-plane',
          icon: createFeatureTreeMenuIcon('action-create-datum-plane'),
        },
      ];
    }
    return [];
  }

  if (nodeType === 'datum-plane') {
    const referencePlaneEditActions: DropdownOption[] = nodeMeta?.canEditReferencePlane
      ? [{
        label: '编辑特征',
        key: 'edit-feature',
        icon: createFeatureTreeMenuIcon('action-edit-feature'),
      }, {
        type: 'divider',
        key: 'd-edit-reference-plane',
      }]
      : [];
    return [
      ...referencePlaneEditActions,
      {
        label: '草图绘制',
        key: 'enter-sketch',
        icon: createFeatureTreeMenuIcon('action-enter-sketch'),
      },
      {
        label: '正视于',
        key: 'normal-to',
        icon: createFeatureTreeMenuIcon('action-normal-to'),
      },
      {
        type: 'divider',
        key: 'd0',
      },
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
      {
        label: '删除',
        key: 'delete',
        icon: createFeatureTreeMenuIcon('action-delete'),
      },
      {
        type: 'divider',
        key: 'd1',
      },
      visibilityOption,
    ];
  }

  if (nodeType === 'datum-axis') {
    const referenceAxisEditActions: DropdownOption[] = nodeMeta?.canEditReferenceAxis
      ? [{
        label: '编辑特征',
        key: 'edit-feature',
        icon: createFeatureTreeMenuIcon('action-edit-feature'),
      }, {
        type: 'divider',
        key: 'd-edit-reference-axis',
      }]
      : [];
    return [
      ...referenceAxisEditActions,
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
      {
        label: '删除',
        key: 'delete',
        icon: createFeatureTreeMenuIcon('action-delete'),
      },
      {
        type: 'divider',
        key: 'd1-axis',
      },
      visibilityOption,
    ];
  }

  if (nodeType === 'datum-point') {
    const referencePointEditActions: DropdownOption[] = nodeMeta?.canEditReferencePoint
      ? [{
        label: '编辑特征',
        key: 'edit-feature',
        icon: createFeatureTreeMenuIcon('action-edit-feature'),
      }, {
        type: 'divider',
        key: 'd-edit-reference-point',
      }]
      : [];
    return [
      ...referencePointEditActions,
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
      {
        label: '删除',
        key: 'delete',
        icon: createFeatureTreeMenuIcon('action-delete'),
      },
      {
        type: 'divider',
        key: 'd1-point',
      },
      visibilityOption,
    ];
  }

  if (nodeType === 'sketch') {
    const isAbsorbedSketchRef = nodeMeta?.objectType === 'sketch' && nodeMeta.nodeRole === 'sketchRef';
    return [
      {
        label: '编辑草图',
        key: 'edit-sketch',
        icon: createFeatureTreeMenuIcon('action-edit-sketch'),
      },
      {
        label: '正视于',
        key: 'normal-to',
        icon: createFeatureTreeMenuIcon('action-normal-to'),
      },
      {
        type: 'divider',
        key: 'd-edit',
      },
      {
        label: '复制草图',
        key: 'copy-sketch',
        icon: createFeatureTreeMenuIcon('action-copy-sketch'),
      },
      {
        type: 'divider',
        key: 'd0',
      },
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
      ...(!isAbsorbedSketchRef
        ? [{
          label: '删除',
          key: 'delete',
          icon: createFeatureTreeMenuIcon('action-delete'),
        }]
        : []),
      {
        type: 'divider',
        key: 'd1',
      },
      visibilityOption,
    ];
  }

  if (nodeType === 'origin') {
    return [
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
    ];
  }

  if (nodeMeta?.objectType === 'feature') {
    if (nodeMeta.rolledBack === true) {
      return [
        {
          label: '向前推进',
          key: 'roll-forward',
          icon: createFeatureTreeMenuIcon('action-roll-forward'),
        },
        {
          label: '退回到前',
          key: 'roll-to-previous',
          icon: createFeatureTreeMenuIcon('action-roll-previous'),
        },
        {
          label: '退回到尾',
          key: 'roll-to-end',
          icon: createFeatureTreeMenuIcon('action-roll-end'),
        },
        {
          type: 'divider',
          key: 'd-rollback-delete',
        },
        {
          label: '删除',
          key: 'delete',
          icon: createFeatureTreeMenuIcon('action-delete'),
        },
      ];
    }

    const featureActions: DropdownOption[] = [];
    featureActions.push({
      label: '退回',
      key: 'rollback-to-feature',
      icon: createFeatureTreeMenuIcon('action-rollback'),
    });
    if (nodeMeta.canEditFeature) {
      featureActions.push({
        label: '编辑特征',
        key: 'edit-feature',
        icon: createFeatureTreeMenuIcon('action-edit-feature'),
      });
    }
    if (nodeMeta.canEditSketch) {
      featureActions.push({
        label: '编辑草图',
        key: 'edit-sketch',
        icon: createFeatureTreeMenuIcon('action-edit-sketch'),
      });
    }

    return [
      ...featureActions,
      ...(featureActions.length > 0
        ? [{
          type: 'divider' as const,
          key: 'd-edit',
        }]
        : []),
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
      {
        label: '删除',
        key: 'delete',
        icon: createFeatureTreeMenuIcon('action-delete'),
      },
      {
        type: 'divider',
        key: 'd1',
      },
      visibilityOption,
    ];
  }

  if (nodeType === 'assembly-mate') {
    return [
      {
        label: '编辑配合',
        key: 'edit-mate',
        icon: createFeatureTreeMenuIcon('action-edit-feature'),
      },
      createAssemblyMateSuppressMenuOption(targetNode),
      {
        label: '删除',
        key: 'delete',
        icon: createFeatureTreeMenuIcon('action-delete'),
      },
    ];
  }

  if (nodeType === 'assembly-mate-folder') {
    return [
      {
        label: '锁定同轴心旋转',
        key: 'lock-concentric-rotation',
        icon: createFeatureTreeMenuIcon('assembly-mate-lock-rotation'),
      },
    ];
  }

  if (isLinearComponentPatternContextMenuGroup.value) {
    return [
      {
        label: '编辑特征',
        key: 'edit-feature',
        icon: createFeatureTreeMenuIcon('action-edit-feature'),
      },
      {
        type: 'divider',
        key: 'd-edit',
      },
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
      {
        label: '删除',
        key: 'delete',
        icon: createFeatureTreeMenuIcon('action-delete'),
      },
    ];
  }

  if (nodeMeta?.canFixOrFloat) {
    const isFixed = nodeMeta.assemblyFixationState === AssemblyComponentFixationState.Fixed;
    return [
      {
        label: isFixed ? '浮动' : '固定',
        key: isFixed ? 'float-component' : 'fix-component',
        icon: createFeatureTreeMenuIcon(isFixed ? 'action-float' : 'action-fix'),
      },
      {
        type: 'divider',
        key: 'd-fixation',
      },
      {
        label: '重命名',
        key: 'rename',
        icon: createFeatureTreeMenuIcon('action-rename'),
      },
      {
        label: '删除',
        key: 'delete',
        icon: createFeatureTreeMenuIcon('action-delete'),
      },
      {
        type: 'divider',
        key: 'd1',
      },
      visibilityOption,
    ];
  }

  if (!props.targetKey) {
    return [];
  }

  return [
    {
      label: '重命名',
      key: 'rename',
      icon: createFeatureTreeMenuIcon('action-rename'),
    },
    {
      label: '删除',
      key: 'delete',
      icon: createFeatureTreeMenuIcon('action-delete'),
    },
    {
      type: 'divider',
      key: 'd1',
    },
    visibilityOption,
  ];
}

const contextMenuOptions = computed<DropdownOption[]>(() => (
  filterReadonlyPreviewContextMenuOptions(buildContextMenuOptions())
));

const dropdownVisible = computed(() => props.show && contextMenuOptions.value.length > 0);

/**
 * 关闭右键菜单。
 */
function closeContextMenu(): void {
  emit('update:show', false);
}

/**
 * 处理点击菜单外区域。
 */
function handleClickOutside(): void {
  closeContextMenu();
}

/**
 * 处理右键菜单动作。
 *
 * @param key 菜单键。
 */
function handleContextMenuSelect(key: string | number): void {
  closeContextMenu();
  if (isReadonlyPreviewBlockedContextMenuKey(key)) {
    return;
  }

  const targetKey = props.targetKey;
  switch (String(key)) {
    case 'rollback-to-feature':
      void featureTreeStore.handleFeatureRollbackToFeature(targetKey);
      return;
    case 'roll-forward':
      void featureTreeStore.handleFeatureRollbackForward(targetKey);
      return;
    case 'roll-to-previous':
      void featureTreeStore.handleFeatureRollbackToPrevious();
      return;
    case 'roll-to-end':
      void featureTreeStore.handleFeatureRollbackToEnd();
      return;
    case 'edit-feature':
      void featureTreeStore.handleFeatureEdit(targetKey);
      return;
    case 'edit-sketch':
      void featureTreeStore.handleFeatureEditSketch(targetKey);
      return;
    case 'edit-mate':
      void featureTreeStore.handleAssemblyMateEdit(targetKey);
      return;
    case 'toggle-flat-tree-view':
      void featureTreeStore.handleToggleFlatTreeView(!showFlatTreeView.value);
      return;
    case 'create-datum-plane':
      featureTreeStore.handleCreateDatumPlane();
      return;
    case 'enter-sketch':
      if (contextMenuPlaneId.value) {
        featureTreeStore.handleEnterDatumPlaneSketch(contextMenuPlaneId.value);
      }
      return;
    case 'normal-to':
      featureTreeStore.handleFeatureTreeNormalTo(targetKey);
      return;
    case 'copy-sketch':
      void featureTreeStore.handleFeatureCopySketch(targetKey);
      return;
    case 'fix-component':
      void featureTreeStore.handleAssemblyComponentFixationChange(
        targetKey,
        AssemblyComponentFixationState.Fixed,
      );
      return;
    case 'float-component':
      void featureTreeStore.handleAssemblyComponentFixationChange(
        targetKey,
        AssemblyComponentFixationState.Floating,
      );
      return;
    case 'rename':
      void featureTreeStore.handleFeatureRename(targetKey, resolveContextMenuTargetLabel());
      return;
    case 'delete':
      void featureTreeStore.handleFeatureDelete(targetKey);
      return;
    case 'hide':
      void featureTreeStore.handleFeatureHide(targetKey);
      return;
    case 'show':
      void featureTreeStore.handleFeatureShow(targetKey);
      return;
    case 'suppress-mate':
      void featureTreeStore.handleAssemblyMateSuppressChange(targetKey, true);
      return;
    case 'unsuppress-mate':
      void featureTreeStore.handleAssemblyMateSuppressChange(targetKey, false);
      return;
    case 'lock-concentric-rotation':
      void featureTreeStore.handleAssemblyMateFolderLockConcentricRotation(targetKey);
      return;
    default:
      return;
  }
}
</script>
